// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import API from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const mountedRef = useRef(false);
  const [user, setUser] = useState(null);
  const [impersonating, setImpersonating] = useState(() => {
    try { return !!localStorage.getItem('admin_token'); } catch (e) { return false; }
  });
  const [loading, setLoading] = useState(true);

  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('token') || null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Keep API Authorization header in sync (only modify common header)
  useEffect(() => {
    if (token) {
      API.defaults = API.defaults || {};
      API.defaults.headers = API.defaults.headers || {};
      API.defaults.headers.common = API.defaults.headers.common || {};
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      try { localStorage.setItem('token', token); } catch (e) { /* ignore */ }
    } else {
      if (API.defaults && API.defaults.headers && API.defaults.headers.common) {
        delete API.defaults.headers.common['Authorization'];
      }
      try { localStorage.removeItem('token'); } catch (e) { /* ignore */ }
    }
  }, [token]);

  // Lot F — Session glissante : rafraîchit le token AVANT son expiration tant que
  // l'utilisateur est actif. Évite la déconnexion brutale (token 7 j) et
  // l'expiration en plein parcours (ex. checkout). Le refresh exige un token
  // ENCORE valide côté serveur : aucun revival de token expiré, donc aucune faille.
  useEffect(() => {
    if (!token) return undefined;
    const decode = (tok) => {
      try {
        const part = String(tok).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const pad = part + '==='.slice((part.length + 3) % 4);
        return JSON.parse(atob(pad));
      } catch (e) { return null; }
    };
    const p = decode(token);
    if (!p || !p.exp) return undefined;
    const expMs = p.exp * 1000;
    const lifeMs = p.iat ? (p.exp - p.iat) * 1000 : 7 * 24 * 3600 * 1000;
    // On rafraîchit dans le dernier tiers de vie du token, plafonné à 24 h.
    const threshold = Math.min(24 * 3600 * 1000, Math.max(60 * 1000, lifeMs / 3));
    let refreshing = false;
    const maybeRefresh = async () => {
      const left = expMs - Date.now();
      if (left <= 0 || left >= threshold || refreshing) return;
      refreshing = true;
      try {
        const res = await API.post('/auth/refresh');
        const newTok = res?.data?.token || (res?.data?.data && res.data.data.token) || null;
        if (newTok && mountedRef.current) {
          try { localStorage.setItem('token', newTok); } catch (e) { /* ignore */ }
          setToken(newTok);
        }
      } catch (e) { /* échec -> si réellement expiré, l'intercepteur 401 gère */ }
      finally { refreshing = false; }
    };
    maybeRefresh(); // au montage / changement de token
    const iv = setInterval(maybeRefresh, 30 * 60 * 1000); // contrôle toutes les 30 min
    const onFocus = () => { maybeRefresh(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [token]);

  // Helper to normalize API response shapes for user object
  const normalizeUserPayload = (resData) => {
    // common shapes:
    // { data: { user: {...}, token: '...' } }
    // { user: {...}, token: '...' }
    // { data: {...} } where data is user
    if (!resData) return null;
    if (resData.user) return resData.user;
    if (resData.data && resData.data.user) return resData.data.user;
    if (resData.data && typeof resData.data === 'object' && !resData.data.token) return resData.data;
    // sometimes the payload is directly user
    if (typeof resData === 'object' && !resData.token) return resData;
    return null;
  };

  // `silent: true` -> ne touche PAS a l'etat global `loading`.
  //
  // Pourquoi c'est indispensable : MerchantRoute et AdminRoute affichent un
  // spinner plein ecran des que `loading` passe a true, ce qui DEMONTE tout
  // l'arbre (layout compris). Un composant monte dans le layout qui appelle
  // refreshUser() se demonte donc lui-meme, se remonte quand le chargement se
  // termine, rappelle refreshUser()... boucle infinie visible a l'ecran
  // (la page "va et vient") jusqu'a ce qu'un appel echoue et deconnecte.
  // Un rafraichissement en arriere-plan ne doit jamais faire clignoter l'UI.
  const loadUser = useCallback(async (options = {}) => {
    const silent = options && options.silent === true;
    if (!silent) setLoading(true);
    if (!token) {
      if (mountedRef.current) {
        setUser(null);
        if (!silent) setLoading(false);
      }
      return null;
    }

    try {
      const res = await API.get('/auth/me');
      if (!mountedRef.current) return null;
      const payload = res?.data || null;
      const fetchedUser = normalizeUserPayload(payload);
      if (fetchedUser) {
        setUser(fetchedUser);
        return fetchedUser;
      }
      // Reponse 200 mais sans utilisateur exploitable : session invalide.
      setUser(null);
      setToken(null);
      return null;
    } catch (err) {
      const status = err?.response?.status;
      console.warn('loadUser error:', err?.response?.data || err.message || err);
      if (!mountedRef.current) return null;

      // On ne deconnecte QUE sur un refus d'authentification explicite.
      // Avant, n'importe quelle erreur (coupure reseau, 500 passager, 429 du
      // rate limiter) supprimait le token : le marchand etait ejecte sur place
      // pour un simple hoquet de connexion, alors que sa session etait valide.
      if (status === 401 || status === 403) {
        setUser(null);
        setToken(null);
        try { localStorage.removeItem('token'); } catch (e) {}
        return null;
      }

      // Erreur technique : on garde la session et l'utilisateur deja charge.
      return null;
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Chargement initial : non silencieux (on veut le spinner au demarrage).
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email, password) => {
    try {
      const res = await API.post('/auth/login', { email, password });
      const data = res?.data || {};
      // P3-A : 2e étape requise -> on remonte le tempToken sans rien stocker
      if (data.requiresTwoFA) return { success: true, requiresTwoFA: true, tempToken: data.tempToken };
      // normalize common shapes
      const tokenValue = data.token || (data.data && data.data.token) || data.accessToken || null;
      const userValue = normalizeUserPayload(data) || normalizeUserPayload(data.data) || null;

      if (tokenValue) { try { localStorage.setItem('token', tokenValue); } catch (e) { /* ignore */ } setToken(tokenValue); }
      if (mountedRef.current && userValue) setUser(userValue);

      return { success: true, token: tokenValue, user: userValue, raw: data };
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'Erreur de connexion';
      const error = new Error(typeof serverMsg === 'string' ? serverMsg : JSON.stringify(serverMsg));
      error.response = err?.response;
      throw error;
    }
  }, []);

  // P3-A : 2e étape du login (code TOTP ou code de secours)
  const confirmTwoFA = useCallback(async (tempToken, totpCode) => {
    try {
      const res = await API.post('/auth/2fa/confirm', { tempToken, totpCode });
      const data = res?.data || {};
      const tokenValue = data.token || (data.data && data.data.token) || null;
      const userValue = normalizeUserPayload(data) || normalizeUserPayload(data.data) || null;
      if (tokenValue) { try { localStorage.setItem('token', tokenValue); } catch (e) {} setToken(tokenValue); }
      if (mountedRef.current && userValue) setUser(userValue);
      return { success: true, token: tokenValue, user: userValue };
    } catch (err) {
      const serverMsg = err?.response?.data?.message || 'Code incorrect';
      const error = new Error(typeof serverMsg === 'string' ? serverMsg : 'Code incorrect');
      error.response = err?.response;
      throw error;
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const res = await API.post('/auth/register', userData);
      const data = res?.data || {};
      const tokenValue = data.token || (data.data && data.data.token) || null;
      const userValue = normalizeUserPayload(data) || normalizeUserPayload(data.data) || null;
      // Écrire le token de façon SYNCHRONE en localStorage : la requête suivante
      // (ex: création du profil marchand juste après l'inscription Partenaire/Agence)
      // doit être immédiatement authentifiée, sans attendre le useEffect.
      if (tokenValue) { try { localStorage.setItem('token', tokenValue); } catch (e) { /* ignore */ } setToken(tokenValue); }
      if (mountedRef.current && userValue) setUser(userValue);
      return { success: true, token: tokenValue, user: userValue, raw: data };
    } catch (err) {
      const serverMsg = err?.response?.data?.message || err?.message || 'Erreur d\'inscription';
      const error = new Error(serverMsg);
      error.response = err?.response;
      throw error;
    }
  }, []);

  // Connexion immédiate à partir d'un token déjà obtenu (ex: guest checkout).
  // Écrit le token en localStorage de façon synchrone pour que la requête
  // suivante (initiation paiement) soit immédiatement authentifiée.
  const applyAuth = useCallback(({ token: tk, user: usr } = {}) => {
    if (tk) {
      try { localStorage.setItem('token', tk); } catch (e) { /* ignore */ }
      setToken(tk);
    }
    if (usr && mountedRef.current) setUser(usr);
  }, []);

  const impersonate = useCallback(({ token: tk, user: usr } = {}) => {
    if (!tk) return;
    try {
      const current = localStorage.getItem('token');
      if (current && !localStorage.getItem('admin_token')) localStorage.setItem('admin_token', current);
      localStorage.setItem('token', tk);
    } catch (e) { /* ignore */ }
    setToken(tk);
    setImpersonating(true);
    if (usr && mountedRef.current) setUser(usr);
  }, []);

  const stopImpersonation = useCallback(async () => {
    let adminTk = null;
    try { adminTk = localStorage.getItem('admin_token'); } catch (e) {}
    if (!adminTk) return false;
    try {
      localStorage.setItem('token', adminTk);
      localStorage.removeItem('admin_token');
    } catch (e) { /* ignore */ }
    setToken(adminTk);
    setImpersonating(false);
    await loadUser();
    return true;
  }, [loadUser]);

  const logout = useCallback(async () => {
  try {

    setUser(null);
    setToken(null);

    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    } catch {}

    if (API?.defaults?.headers?.common) {
      delete API.defaults.headers.common.Authorization;
      delete API.defaults.headers.common.authorization;
    }

  } catch (err) {
    console.warn("logout error:", err);
  }
}, []);


  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    applyAuth,
    logout,
    refreshUser: loadUser,
    confirmTwoFA,
    impersonate,
    stopImpersonation,
    isImpersonating: impersonating,
    isAuthenticated: !!user
  }), [user, token, loading, login, register, applyAuth, logout, loadUser, confirmTwoFA, impersonate, stopImpersonation, impersonating]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
